import * as types from "./types";
import * as actions from "./actions";

import { AsyncStorage } from "react-native";

import {
  apiCall,
  GET_EMBED_SURVEY_DIVISION_URL,
  SURVEY_INVITES_URL,
  SURVEY_OPEN_SIGNAL_URL,
} from "apis";
import {
  getOrganization,
  setOrganization,
  addDivisions,
} from "state/modules/organization";
import {
  addQuestionnaire,
  getQuestionnaire,
} from "state/modules/questionnaires";
import { preSelectOptions } from "state/modules/response";
import { startSurvey } from "state/modules/navigation";
import { setDivision } from "state/modules/division";
import { clearState } from "state/modules/root";
import { createError } from "state/modules/error";
import { setName, setCustomer } from "state/modules/customer";
import { INVITES_STORE_ID } from "consts";
import {
  ItemListDeserializer,
  PreviousResponseDeserializer,
  WebSurveyOrganizationDeserializer,
  PreviousStagesResponseDeserializer,
} from "serializers";
import { getQuizScore } from "../response/selectors";
import { isStandardApp } from "./selectors";
import init from "./init";

export {
  setInviteId,
  setSurveyStatus,
  setLinkedOptionForItem,
  removeLinkedOption,
  setSurveyMessage,
} from "./actions";

export const STATUS = {
  PENDING: "pending",
  IN_PROGRESS: "in_progress",
  RESULT: "result",
  SUBMITTING: "submitting",
  SUBMITTED: "submitted",
  EXPIRED: "expired",
  NOT_FOUND: "not_found",
  RECEIVED: "received",
};

export const selectQuestionnaire = () => (dispatch, getState) => {
  const { questionnaires } = getState();
  const percentageSum = questionnaires.reduce((a, b) => a + b.percentage, 0);
  let randomPercentage = Math.floor(Math.random() * percentageSum) + 1;
  const questionnaire = questionnaires.find(q => {
    randomPercentage = randomPercentage - q.percentage;
    return randomPercentage <= 0;
  });
  if (questionnaire) {
    dispatch(setQuestionnaire(questionnaire));
  }
};

export const setQuestionnaire = questionnaire => dispatch => {
  dispatch(actions.setQuestionnaire(questionnaire.id));
  questionnaire.for_go && dispatch(actions.setForGo(true));
  questionnaire.is_quiz && dispatch(actions.setQuizFlag());
  dispatch(actions.setSurveyStatus(STATUS.PENDING));
  dispatch(preSelectOptions(questionnaire));
  dispatch(checkAutoStart(questionnaire));
};

export const startQuestionnaire = () => dispatch => {
  dispatch(actions.setSurveyStatus(STATUS.IN_PROGRESS));
  dispatch(actions.setStartTime(new Date().getTime()));
};

export const sendSurveyOpenSignal = () => (dispatch, getState) => {
  const { survey } = getState();

  dispatch(
    apiCall({
      type: types.POST_SURVEY_OPEN_SIGNAL,
      payload: {
        url: SURVEY_OPEN_SIGNAL_URL,
        data: { invite: survey.inviteId },
        onSuccess: _ => dispatch => {},
        onError: _ => dispatch => {},
      },
    }),
  );
};

export const discardSurvey = () => (dispatch, getState) => {
  const state = getState();
  const modules = ["navigation"];

  if (isStandardApp(state)) {
    modules.push("customer", "response", "survey");
  }
  dispatch(clearState(modules));
};

export const clearSurvey = () => (dispatch, getState) => {
  const state = getState();
  const modules = ["navigation", "response"];

  if (isStandardApp(state)) {
    modules.push("survey", "customer");
  }
  return dispatch(clearState(modules));
};

export const getWebSurvey = (hash, embedSurveyProps = {}) => dispatch => {
  dispatch(
    apiCall({
      type: types.GET_WEB_SURVEY,
      payload: {
        url: SURVEY_INVITES_URL(hash),
        onSuccess: webSurveySetup(embedSurveyProps),
        onError: onWebSurveyError,
      },
    }),
  );
};

const onWebSurveyError = error => dispatch => {
  if (error && error.status === 404) {
    dispatch(actions.setSurveyStatus(STATUS.NOT_FOUND));
    dispatch(
      createError({
        type: types.GET_WEB_SURVEY,
        message: "Survey not found",
      }),
    );
  }
};

const isAlreadySubmitted = async inviteId => {
  let submittedInvites = await AsyncStorage.getItem(INVITES_STORE_ID);
  submittedInvites = JSON.parse(submittedInvites) || {};
  return submittedInvites[inviteId];
};

const redirectToExpirableLink = expirableLinkUUID => {
  const webLinkPathPrefix = __DEV__ ? "" : "/web-survey";
  window.history.replaceState(
    null,
    null,
    `${webLinkPathPrefix}/#/${expirableLinkUUID}`,
  );
};

const webSurveySetup = embedSurveyProps => data => async dispatch => {
  const {
    status,
    questionnaire,
    organization,
    id,
    division,
    customer,
    prevent_invite_id,
    encoded_uuid,
    type: invite_type,
  } = data;

  if (questionnaire.redirect_web_link) {
    redirectToExpirableLink(encoded_uuid);
  }

  const _isAlreadySubmitted = await isAlreadySubmitted(prevent_invite_id);

  if (!organization.is_active) {
    dispatch(actions.setSurveyStatus(STATUS.EXPIRED));
    dispatch(
      actions.setSurveyMessage(
        "Survey access is restricted due to the organization being inactive.",
      ),
    );
    return;
  }

  const isSubmitted =
    questionnaire?.prevent_multiple_submissions &&
    _isAlreadySubmitted &&
    invite_type !== "EMBED";
  if (status === "E" || status === "R" || isSubmitted) {
    const message = isSubmitted
      ? "You have already submitted your feedback."
      : data.message;
    dispatch(actions.setSurveyStatus(STATUS.EXPIRED));
    dispatch(actions.setSurveyMessage(message));
    return;
  }
  if (!organization.is_active) {
    dispatch(actions.setSurveyStatus(STATUS.EXPIRED));
    dispatch(
      actions.setSurveyMessage(
        "Survey access is restricted due to the organization being inactive.",
      ),
    );
    return;
  }

  if (customer) {
    dispatch(setCustomer(customer));
  }

  if (division) {
    dispatch(setDivision({ id: division.id, title: division.title }));
  } else {
    const { customer_info } = questionnaire;
    const divisionInfo = customer_info?.find(field => field.key === "division");
    if (divisionInfo) {
      dispatch(getDivisions(divisionInfo.api));
      dispatch(actions.setDivisionInfo(divisionInfo));
    }
  }

  const items = ItemListDeserializer(data.items);
  dispatch(actions.storeItems(items));

  const previousFeedbackResponse = PreviousResponseDeserializer(
    data.ask_once_questions,
  );

  const previousStagesResponse = PreviousStagesResponseDeserializer(
    data.dependency_checker_json,
  );

  const org = WebSurveyOrganizationDeserializer(organization);

  await dispatch(setOrganization(org));
  dispatch(addQuestionnaire(questionnaire));
  dispatch(actions.setInviteId(id));
  dispatch(actions.setPreventInviteId(prevent_invite_id));
  dispatch(actions.setPreviousResponse(previousFeedbackResponse));
  dispatch(actions.setStageNumber(data.stage_number));
  dispatch(actions.setLastFeedbackId(data.last_feedback_id));
  dispatch(actions.setPreviousStagesResponse(previousStagesResponse));

  dispatch(
    actions.setSurveyFields({
      placeholders: data.placeholders || init.placeholders,
    }),
  );

  if (invite_type === "EMBED") {
    dispatch(setupAppEmbedSurvey(embedSurveyProps));
  }

  dispatch(selectQuestionnaire());
  dispatch(sendSurveyOpenSignal());
};

export const getDivisions = api => dispatch => {
  dispatch(
    apiCall({
      type: types.GET_DIVISIONS,
      payload: {
        url: api.slice(3), // ignore 'api' at the start
        onSuccess: divisions => dispatch => dispatch(addDivisions(divisions)),
      },
    }),
  );
};

export const getDivisionForEmbedSurvey = api => dispatch => {
  dispatch(
    apiCall({
      type: types.GET_DIVISION_FOR_EMBED_SURVEY,
      payload: {
        url: api.slice(3), // ignore 'api' at the start
        onSuccess: division => dispatch => dispatch(setDivision(division)),
        onError: error => dispatch => {
          /*console.log({ error })*/
        },
      },
    }),
  );
};

export const updateSurveyScore = () => (dispatch, getState) => {
  const responses = getState().response.questions || {};

  const scores = [];
  for (const qId in responses) {
    const score = responses[qId].score;
    if (typeof score === "number") {
      scores.push(score);
    }
  }

  if (!scores.length) {
    return;
  }

  // score => 0: positive, 1: neutral, 2: negative
  const maxScore = Math.max(...scores);
  dispatch(actions.setSurveyScore(maxScore));
};

export const hideItemList = () => dispatch => {
  dispatch(actions.hideItemList());
  setTimeout(() => dispatch(actions.removeLinkedOption()), 400);
};

export const setQuizScore = () => (dispatch, getState) => {
  const state = getState();
  const [total, score] = getQuizScore(state);
  dispatch(actions.setQuizScore(total, score));
};

export const startSurveyDemo = (
  slug,
  questionnaireId,
  customer,
) => async dispatch => {
  dispatch(actions.setIsDemo(true));
  await dispatch(getOrganization(slug));
  await dispatch(getQuestionnaire(questionnaireId));
  dispatch(setName(customer));
  dispatch(selectQuestionnaire());
};

const checkAutoStart = questionnaire => dispatch => {
  if (questionnaire.auto_start) {
    dispatch(actions.setAutoStart(true));
    dispatch(startSurvey());
  }
};

export const setupAppEmbedSurvey = ({
  respondentHash,
  divisionQueryString,
}) => (dispatch, getState) => {
  const { organization } = getState();

  let respondentInfo = null;
  try {
    respondentInfo = JSON.parse(window.atob(respondentHash, "base64"));
  } catch (err) {
    // console.log({ err });
  }

  if (!respondentInfo) {
    dispatch(actions.setSurveyStatus(STATUS.EXPIRED));
    dispatch(actions.setSurveyMessage("Respondent Hash has invalid format"));
    return;
  }

  dispatch(actions.setIsAppEmbed(true));
  dispatch(setCustomer(respondentInfo));
  dispatch(
    getDivisionForEmbedSurvey(
      GET_EMBED_SURVEY_DIVISION_URL(organization.id, divisionQueryString),
    ),
  );
};

export const setSubmittedBy = userID => dispatch => {
  dispatch(actions.setSubmittedBy(userID));
};
